import { DesignSystemToken } from 'designSystem/types';

export interface ColorFamily {
	family: string;
	shades: { shade: string; token: DesignSystemToken }[];
}

export interface ColorGridData {
	families: ColorFamily[];
	allShades: string[];
	ungrouped: DesignSystemToken[];
}

// Parses "gray-100" from "--ds-gray-100" or "--theme-color-gray-100"
// returns { family, shade } or null if it doesn't match a family-shade pattern
function parseFamilyShade(cssVariable: string): { family: string; shade: string } | null {
	const stripped = cssVariable.replace(/^--(ds|theme)-/, '');
	const match = stripped.match(/^(?:color-)?(.+)-(\d+)$/);
	if (!match) return null;
	return { family: match[1], shade: match[2] };
}

// Groups color tokens into a grid structure (family rows x shade columns)
// and collects tokens that don't fit the pattern into ungrouped
export function groupColorsForGrid(tokens: DesignSystemToken[]): ColorGridData {
	const familyMap = new Map<string, { shade: string; token: DesignSystemToken }[]>();
	const ungrouped: DesignSystemToken[] = [];
	const shadeSet = new Set<string>();

	for (const token of tokens) {
		const parsed = parseFamilyShade(token.cssVariable);
		if (parsed) {
			if (!familyMap.has(parsed.family)) {
				familyMap.set(parsed.family, []);
			}
			familyMap.get(parsed.family)!.push({ shade: parsed.shade, token });
			shadeSet.add(parsed.shade);
		} else {
			ungrouped.push(token);
		}
	}

	const allShades = Array.from(shadeSet).sort((a, b) => Number(a) - Number(b));

	const families: ColorFamily[] = Array.from(familyMap.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([family, shades]) => ({
			family,
			shades: shades.sort((a, b) => Number(a.shade) - Number(b.shade)),
		}));

	return { families, allShades, ungrouped };
}
